import React, { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useForm, FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import emailjs from "@emailjs/browser";
import { toast } from "react-hot-toast";

import ProgressBar from "./components/ProgressBar";
import FormStep1 from "./components/FormStep1";
import FormStep2 from "./components/FormStep2";
import FormStep3 from "./components/FormStep3";
import FormNavButtons from "./components/FormNavButtons";
import SuccessMessage from "./components/SuccessMessage";
import { useFormPersist } from "./hooks/useFormPersist";

const initialFormState = {
  firstName: "",
  lastName: "",
  email: "",
  country: "",
  interest: "",
  capacity: "",
  phoneNumber: "",
  message: "",
  companyName: "",
};

function ContactForm() {
  const { t } = useTranslation();
  const copy = t("components.contactUs.contactForm", { returnObjects: true });

  const [
    step,
    setStep,
    isSubmitted,
    setIsSubmitted,
    persistedFormData,
    setPersistedFormData,
  ] = useFormPersist("contactForm", 1, false, initialFormState);

  const [stepValidationAttempted, setStepValidationAttempted] = useState(false);

  const methods = useForm({
    defaultValues: persistedFormData,
    mode: "onSubmit",
    reValidateMode: "onChange",
  });
  const { reset, handleSubmit, watch, trigger, clearErrors } = methods;

  useEffect(() => {
    setStepValidationAttempted(false);
    clearErrors();
  }, [step, clearErrors]);

  useEffect(() => {
    const subscription = watch((value) => {
      setPersistedFormData(value);
    });
    return () => subscription.unsubscribe();
  }, [watch, setPersistedFormData]);

  useEffect(() => {
    emailjs.init(import.meta.env.VITE_EMAILJS_PUBLIC_KEY);
  }, []);

  const handleFieldChange = useCallback(() => {
    if (stepValidationAttempted) {
      requestAnimationFrame(() => {
        let fieldsToValidate = [];

        if (step === 1) {
          fieldsToValidate = ["firstName", "lastName", "email"];
        } else if (step === 2) {
          fieldsToValidate = ["country", "phoneNumber"];
        } else if (step === 3) {
          fieldsToValidate = ["interest", "message"];
        }

        trigger(fieldsToValidate);
      });
    }
  }, [stepValidationAttempted, step, trigger]);

  const onSubmit = async (data) => {
    try {
      setIsSubmitted(true);

      const formElement = document.createElement("form");

      const completeData = { ...persistedFormData, ...data };

      Object.entries(completeData).forEach(([key, value]) => {
        if (value) {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = key;
          input.value = value;
          formElement.appendChild(input);
        }
      });
      await emailjs.sendForm(
        import.meta.env.VITE_EMAILJS_SERVICE_ID,
        import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
        formElement,
        { publicKey: import.meta.env.VITE_EMAILJS_PUBLIC_KEY }
      );

      sessionStorage.removeItem("contactForm");
    } catch (error) {
      console.error("Error sending email:", error);
      setIsSubmitted(false);
      toast.error(
        copy.errorMessages.submissionFailed ||
          "Failed to send message. Please try again."
      );
    }
  };

  const handleResetForm = () => {
    reset(initialFormState);
    setStep(1);
    setIsSubmitted(false);
    setPersistedFormData(initialFormState);
    sessionStorage.removeItem("contactForm");
  };

  const handleContinue = async (nextStep) => {
    setStepValidationAttempted(true);

    let fieldsToValidate = [];

    if (step === 1) {
      fieldsToValidate = ["firstName", "lastName", "email"];
    } else if (step === 2) {
      fieldsToValidate = ["country", "phoneNumber"];
    } else if (step === 3) {
      fieldsToValidate = ["interest", "message"];
    }

    const isValid = await trigger(fieldsToValidate);

    if (isValid) {
      setStep(nextStep);
      setStepValidationAttempted(false);
    }
  };

  return (
    <div className="py-12 px-4 bg-black text-gray-200">
      <motion.div className="max-w-2xl mx-auto p-8 bg-gray-800 rounded-xl shadow-lg space-y-8">
        {isSubmitted ? (
          <SuccessMessage copy={copy} onReset={handleResetForm} />
        ) : (
          <FormProvider {...methods}>
            <motion.h2 className="text-2xl font-bold mb-8 text-center">
              {copy.formTitle}
            </motion.h2>

            <ProgressBar
              currentStep={step}
              totalSteps={3}
              labels={copy.progressSteps}
            />

            <form
              onSubmit={handleSubmit(onSubmit)}
              className="space-y-8"
              onChange={handleFieldChange}
            >
              {step === 1 && (
                <FormStep1 copy={copy} showErrors={stepValidationAttempted} />
              )}
              {step === 2 && (
                <FormStep2 copy={copy} showErrors={stepValidationAttempted} />
              )}
              {step === 3 && (
                <FormStep3 copy={copy} showErrors={stepValidationAttempted} />
              )}

              <FormNavButtons
                step={step}
                onContinue={handleContinue}
                onBack={() => setStep(step - 1)}
                copy={copy}
              />
            </form>
          </FormProvider>
        )}
      </motion.div>
    </div>
  );
}

export default React.memo(ContactForm);
